/**
 * @class wix-data.WixDataQueryReferencedResult
 * @summary The results of a data reference query, containing the retrieved items.
 * @classdesc
 *  When you execute a reference query with the [`queryReferenced()`](wix-data.html#queryReferenced)
 *  function, it returns a Promise that resolves to a `WixDataQueryReferencedResult`
 *  object. This object contains the items that match the reference query and
 *  functions for paging through the query results.
 */
"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _createClass = (function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; })();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var WixDataQueryReferencedResult = (function () {
  function WixDataQueryReferencedResult(provider, collectionName, referenceAttribute, holdingItemId, result) {
    var options = arguments.length <= 5 || arguments[5] === undefined ? {} : arguments[5];

    _classCallCheck(this, WixDataQueryReferencedResult);

    this.provider = provider;
    this.collectionName = collectionName;
    this.referenceAttribute = referenceAttribute;
    this.holdingItemId = holdingItemId;
    this.result = result;
    this.options = options;

    var _options$skip = options.skip;
    var skip = _options$skip === undefined ? 0 : _options$skip;
    var _options$limit = options.limit;
    var limit = _options$limit === undefined ? 50 : _options$limit;

    this.skip = skip;
    this.limit = limit;
  }

  /**
   * @member items
   * @label property
   * @memberof wix-data.WixDataQueryReferencedResult
   * @readonly
   * @type {external:Object[]}
   * @syntax
   * get items(): Object[]
   * @summary Returns the items that match the reference query.
   * @description
   *  The current page of items retrieved by the reference query.
   *
   *  The page size is 50 items. Navigate through pages of items with the
   *  [`prev()`](#prev) and [`next()`](#next) functions.
   *
   *  When no items match the reference query, the `items` array is empty.
   * @snippet [WixDataQueryReferencedResult-items.es6=Get the items of a reference query result]
   * @snippet [WixDataQueryReferencedResult-items_scenario.es6=Perform a reference query and get the items of the result]
   */

  _createClass(WixDataQueryReferencedResult, [{
    key: "hasNext",

    /**
     * @function hasNext
     * @summary Indicates if the reference query has more results.
     * @syntax
     * function hasNext(): boolean
     * @returns {external:Boolean} `true` if there are more results.
     * @memberof wix-data.WixDataQueryReferencedResult
     * @snippet [WixDataQueryReferencedResult-hasNext.es6=Get whether the reference query result object has more results]
     * @instance
     */
    value: function hasNext() {
      return this.skip + this.limit < this.result.totalCount;
    }

    /**
     * @function hasPrev
     * @summary Indicates if the reference query has previous results.
     * @syntax
     * function hasPrev(): boolean
     * @returns {external:Boolean} `true` if there are previous results.
     * @memberof wix-data.WixDataQueryReferencedResult
     * @snippet [WixDataQueryReferencedResult-hasPrev.es6=Get whether the reference query result object has previous results]
     * @instance
     */
  }, {
    key: "hasPrev",
    value: function hasPrev() {
      return this.skip > 0;
    }

    /**
     * @function next
     * @summary Retrieves the next page of reference query results.
     * @syntax
     * function next(): Promise<WixDataQueryReferencedResult>
     * @description
     *  The `next()` function retrieves the next page of reference query results.
     *
     *  The page size is 50 items. Navigate through pages of items with the
     *  [`prev()`](#prev) and [`next()`](#next) functions.
     *
     *  If items are added or removed between calls to `next()` the values returned
     *  may change.
     * @snippet [WixDataQueryReferencedResult-next.es6=Get the next page of a query result]
     * @snippet [WixDataQueryReferencedResult-next_scenario.es6=Iterate through all pages of reference query results]
     * @returns {external:Promise}
     * @fulfill {wix-data.WixDataQueryReferencedResult} A reference query result object with the next page of query results.
     * @reject {external:Error} The errors that caused the rejection.
     * @memberof wix-data.WixDataQueryReferencedResult
     * @instance
     */
  }, {
    key: "next",
    value: function next() {
      return _queryForPage.call(this, this.skip + this.limit);
    }

    /**
     * @function prev
     * @summary Retrieves the previous page of reference query results.
     * @syntax
     * function prev(): Promise<WixDataQueryReferencedResult>
     * @description
     *  The `prev()` function retrieves the previous page of reference query results.
     *
     *  The page size is 50 items. Navigate through pages of items with the
     *  [`prev()`](#prev) and [`next()`](#next) functions.
     *
     *  If items are added or removed between calls to `prev()` the values returned
     *  may change.
     * @snippet [WixDataQueryReferencedResult-prev.es6=Get the previous page of a query result]
     * @returns {external:Promise}
     * @fulfill {wix-data.WixDataQueryReferencedResult} A query result object with the next page of query results.
     * @reject {external:Error} The errors that caused the rejection.
     * @memberof wix-data.WixDataQueryReferencedResult
     * @instance
     */
  }, {
    key: "prev",
    value: function prev() {
      return _queryForPage.call(this, this.skip - this.limit);
    }
  }, {
    key: "items",
    get: function get() {
      return this.result.items;
    }

    /**
     * @member totalCount
     * @label property
     * @memberof wix-data.WixDataQueryReferencedResult
     * @readonly
     * @type {external:Number}
     * @syntax
     * get totalCount(): Number
     * @summary Returns the total number of items that match the reference query.
     * @description
     *  The `totalCount` returns the total number of items that match the reference query,
     *  not just the number of items in the current page.
     * @snippet [WixDataQueryReferencedResult-totalCount.es6=Get the length of a reference query result]
     * @snippet [WixDataQueryReferencedResult-totalCount_scenario.es6=Perform a reference query and get the length of the result]
     */
  }, {
    key: "totalCount",
    get: function get() {
      return this.result.totalCount;
    }
  }]);

  return WixDataQueryReferencedResult;
})();

exports.WixDataQueryReferencedResult = WixDataQueryReferencedResult;

function _queryForPage(skip) {
  var _this = this;

  var actualSkip = skip >= 0 ? skip : 0;
  var optionsToPass = Object.assign({}, this.options, { skip: actualSkip, limit: this.limit });

  return this.provider.queryReferenced(this.collectionName, this.holdingItemId, this.referenceAttribute, optionsToPass).then(function (result) {
    return new WixDataQueryReferencedResult(_this.provider, _this.collectionName, _this.referenceAttribute, _this.holdingItemId, result, optionsToPass);
  });
}